import React, { useState, useCallback, useRef } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Divider,
  Typography,
  message,
  Upload,
  Collapse,
  Row,
  Col,
  Image,
  Modal,
  Popconfirm,
  Switch,
  Slider,
  Select,
  Tag,
  Tooltip
} from 'antd';
import {
  PictureOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  BookOutlined,
  FileTextOutlined,
  TagsOutlined,
  BulbOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import DataManager from './DataManager';

const { Title, Text } = Typography;
const { TextArea } = Input;

// 防抖Hook - 优化版本
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  const callbackRef = useRef(callback);

  // 更新回调引用
  callbackRef.current = callback;

  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  }, [delay]);
};

// 节流Hook
const useThrottle = (callback, delay) => {
  const lastCallRef = useRef(0);

  return useCallback((...args) => {
    const now = Date.now();
    if (now - lastCallRef.current >= delay) {
      lastCallRef.current = now;
      callback(...args);
    }
  }, [callback, delay]);
};

// 默认图片配置 - 与PDFDocument.js保持一致
const DEFAULT_IMAGES = {
  logo: "https://tc.z.wiki/autoupload/kE3Hj2-cUdufB26okiRLHIlCDnqd8YQs4KuJi30eC86yl5f0KlZfm6UsKj-HyTuv/20250613/Y0lN/600X311/titleIcon.jpg",
  background: "https://tc.z.wiki/autoupload/kE3Hj2-cUdufB26okiRLHIlCDnqd8YQs4KuJi30eC86yl5f0KlZfm6UsKj-HyTuv/20250613/6dQn/1456X816/bg.png"
};

const EnhancedControlPanel = ({
  documentData,
  onDocumentDataChange,
  onGlobalFontChange,
  watermarkConfig,
  setWatermarkConfig
}) => {
  const [form] = Form.useForm();
  // 如果用户有上传图片则显示用户图片，否则显示默认图片
  const [logoPreview, setLogoPreview] = useState(
    documentData?.header?.logoUrl && documentData.header.logoUrl.trim() !== ''
      ? documentData.header.logoUrl
      : DEFAULT_IMAGES.logo
  );
  const [backgroundPreview, setBackgroundPreview] = useState(
    documentData?.backgroundImage && documentData.backgroundImage.trim() !== ''
      ? documentData.backgroundImage
      : DEFAULT_IMAGES.background
  );

  // 手风琴状态管理
  const [activeKeys, setActiveKeys] = useState(['quick-words']); // 默认展开单词快速预览
  const [expandedParagraph, setExpandedParagraph] = useState(null);

  // 段落预览相关状态
  const [previewActiveKeys, setPreviewActiveKeys] = useState([]);
  const [editingWord, setEditingWord] = useState(null);
  const [editWordModal, setEditWordModal] = useState(false);

  // 为每个段落的自定义划线单词输入框管理状态
  const [underlinedWordInputs, setUnderlinedWordInputs] = useState({});

  // 管理备用音标字段的显示状态
  const [editingWordField, setEditingWordField] = useState(null);

  // 监听documentData变化，更新预览状态
  React.useEffect(() => {
    if (documentData) {
      setLogoPreview(
        documentData.header?.logoUrl && documentData.header.logoUrl.trim() !== ''
          ? documentData.header.logoUrl
          : DEFAULT_IMAGES.logo
      );
      setBackgroundPreview(
        documentData.backgroundImage && documentData.backgroundImage.trim() !== ''
          ? documentData.backgroundImage
          : DEFAULT_IMAGES.background
      );
    }
  }, [documentData]);

  // 清理防抖定时器
  React.useEffect(() => {
    return () => {
      // 组件卸载时清理本地状态
      setLocalInputValues({});
    };
  }, []);

  // 确保数据结构向后兼容 - 为现有段落添加underlinedWords数组
  React.useEffect(() => {
    if (documentData?.paragraphs) {
      let needsUpdate = false;
      const updatedParagraphs = documentData.paragraphs.map(paragraph => {
        if (!paragraph.underlinedWords) {
          needsUpdate = true;
          return { ...paragraph, underlinedWords: [] };
        }
        return paragraph;
      });

      if (needsUpdate) {
        const newData = { ...documentData, paragraphs: updatedParagraphs };
        onDocumentDataChange(newData);
      }
    }
  }, [documentData?.paragraphs, onDocumentDataChange]);

  // 更新文档数据的通用函数（立即更新）
  const updateDocumentData = useCallback((path, value) => {
    const newData = { ...documentData };
    const keys = path.split('.');
    let current = newData;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    onDocumentDataChange(newData);
  }, [documentData, onDocumentDataChange]);

  // 防抖的更新函数（用于频繁输入）
  const debouncedUpdateDocumentData = useDebounce(updateDocumentData, 200); // 减少延迟提高响应性

  // 优化的本地状态管理 - 减少重新渲染
  const [localInputValues, setLocalInputValues] = useState({});

  // 本地输入处理函数
  const handleLocalInput = useCallback((path, value) => {
    setLocalInputValues(prev => ({ ...prev, [path]: value }));
    debouncedUpdateDocumentData(path, value);
  }, [debouncedUpdateDocumentData]);

  // 获取输入值（优先使用本地状态）
  const getInputValue = useCallback((path, defaultValue = '') => {
    return localInputValues[path] !== undefined ? localInputValues[path] :
           path.split('.').reduce((obj, key) => obj?.[key], documentData) || defaultValue;
  }, [localInputValues, documentData]);

  // 段落预览功能
  const handleWordDelete = useCallback((paragraphIndex, wordIndex) => {
    const newData = { ...documentData };
    newData.paragraphs[paragraphIndex].words.splice(wordIndex, 1);
    onDocumentDataChange(newData);
    message.success('单词已删除');
  }, [documentData, onDocumentDataChange]);

  // 添加自定义划线单词
  const addUnderlinedWords = useCallback((paragraphIndex, wordsString) => {
    if (!wordsString || !wordsString.trim()) return;

    // 解析输入的单词（支持逗号和空格分隔）
    const newWords = wordsString
      .split(/[,\s]+/)
      .map(word => word.trim())
      .filter(word => word.length > 0);

    if (newWords.length === 0) return;

    const newData = { ...documentData };
    const newParagraphs = [...newData.paragraphs];

    // 确保underlinedWords数组存在
    if (!newParagraphs[paragraphIndex].underlinedWords) {
      newParagraphs[paragraphIndex] = {
        ...newParagraphs[paragraphIndex],
        underlinedWords: []
      };
    }

    // 避免重复添加
    const existingWords = new Set(newParagraphs[paragraphIndex].underlinedWords.map(w => w.toLowerCase()));
    const uniqueNewWords = newWords.filter(word => !existingWords.has(word.toLowerCase()));

    if (uniqueNewWords.length > 0) {
      newParagraphs[paragraphIndex] = {
        ...newParagraphs[paragraphIndex],
        underlinedWords: [...newParagraphs[paragraphIndex].underlinedWords, ...uniqueNewWords]
      };

      newData.paragraphs = newParagraphs;
      onDocumentDataChange(newData);
      message.success(`已添加 ${uniqueNewWords.length} 个划线单词`);
    } else {
      message.info('单词已存在，未重复添加');
    }
  }, [documentData, onDocumentDataChange]);

  // 删除自定义划线单词
  const removeUnderlinedWord = useCallback((paragraphIndex, wordIndex) => {
    const newData = { ...documentData };
    const newParagraphs = [...newData.paragraphs];

    if (newParagraphs[paragraphIndex].underlinedWords) {
      const originalWords = [...newParagraphs[paragraphIndex].underlinedWords];
      const deletedWord = originalWords[wordIndex];

      const newUnderlinedWords = [...originalWords];
      newUnderlinedWords.splice(wordIndex, 1);

      newParagraphs[paragraphIndex] = {
        ...newParagraphs[paragraphIndex],
        underlinedWords: newUnderlinedWords
      };

      newData.paragraphs = newParagraphs;
      onDocumentDataChange(newData);
      message.success(`划线单词 "${deletedWord}" 已删除`);
    }
  }, [documentData, onDocumentDataChange]);

  const handleWordEdit = useCallback((paragraphIndex, wordIndex, word) => {
    setEditingWord({ paragraphIndex, wordIndex, word: { ...word } });
    setEditWordModal(true);
  }, []);

  const handleWordSave = useCallback(() => {
    if (editingWord) {
      const newData = { ...documentData };
      newData.paragraphs[editingWord.paragraphIndex].words[editingWord.wordIndex] = editingWord.word;
      onDocumentDataChange(newData);
      setEditWordModal(false);
      setEditingWord(null);
      message.success('单词已更新');
    }
  }, [editingWord, documentData, onDocumentDataChange]);

  // 处理Logo上传
  const handleLogoUpload = (info) => {
    const file = info.file;
    if (file.status === 'done' || file.originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setLogoPreview(imageData);
        updateDocumentData('header.logoUrl', imageData);
        message.success('Logo上传成功');
      };
      reader.readAsDataURL(file.originFileObj || file);
    }
  };

  // 删除Logo - 恢复到默认Logo
  const handleLogoRemove = () => {
    setLogoPreview(DEFAULT_IMAGES.logo);
    updateDocumentData('header.logoUrl', ''); // 设置为空字符串，PDF会使用默认图片
    message.success('已恢复默认Logo');
  };

  // 处理背景图片上传
  const handleBackgroundUpload = (info) => {
    const file = info.file;
    if (file.status === 'done' || file.originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setBackgroundPreview(imageData);
        updateDocumentData('backgroundImage', imageData);
        message.success('背景图片上传成功');
      };
      reader.readAsDataURL(file.originFileObj || file);
    }
  };

  // 删除背景图片 - 恢复到默认背景图片
  const handleBackgroundRemove = () => {
    setBackgroundPreview(DEFAULT_IMAGES.background);
    updateDocumentData('backgroundImage', ''); // 设置为空字符串，PDF会使用默认图片
    message.success('已恢复默认背景图片');
  };

  // 添加新段落
  const addParagraph = () => {
    const newParagraph = {
      english: '',
      chinese: '',
      words: []
    };
    const newParagraphs = [...(documentData.paragraphs || []), newParagraph];
    updateDocumentData('paragraphs', newParagraphs);
    message.success('已添加新段落');
  };

  // 删除段落
  const deleteParagraph = (index) => {
    const newParagraphs = documentData.paragraphs.filter((_, i) => i !== index);
    updateDocumentData('paragraphs', newParagraphs);
    message.success('段落已删除');
  };

  // 更新段落
  const updateParagraph = (index, field, value) => {
    const newParagraphs = [...documentData.paragraphs];
    newParagraphs[index] = { ...newParagraphs[index], [field]: value };
    updateDocumentData('paragraphs', newParagraphs);
  };

  // 添加单词到段落
  const addWordToParagraph = (paragraphIndex) => {
    const newWord = {
      name: '',
      phonetic: '',
      alternativePhonetic: '', // 新增备用音标字段
      partOfSpeech: '',
      example: '',
      scopeWord: '',
      chExample: ''
    };
    const newParagraphs = [...documentData.paragraphs];
    if (!newParagraphs[paragraphIndex].words) {
      newParagraphs[paragraphIndex].words = [];
    }
    newParagraphs[paragraphIndex].words.push(newWord);
    updateDocumentData('paragraphs', newParagraphs);
    message.success('已添加新单词');
  };

  // 删除单词
  const deleteWord = (paragraphIndex, wordIndex) => {
    const newParagraphs = [...documentData.paragraphs];
    newParagraphs[paragraphIndex].words.splice(wordIndex, 1);
    updateDocumentData('paragraphs', newParagraphs);
    message.success('单词已删除');
  };

  // 更新单词
  const updateWord = (paragraphIndex, wordIndex, field, value) => {
    const newParagraphs = [...documentData.paragraphs];
    newParagraphs[paragraphIndex].words[wordIndex] = {
      ...newParagraphs[paragraphIndex].words[wordIndex],
      [field]: value
    };
    updateDocumentData('paragraphs', newParagraphs);
  };

  // 更新中文导读内容
  const updateChineseGuideContent = (index, value) => {
    const newContent = [...(documentData.chineseGuide?.content || [])];
    newContent[index] = value;
    updateDocumentData('chineseGuide.content', newContent);
  };

  // 添加中文导读条目
  const addChineseGuideItem = () => {
    const newContent = [...(documentData.chineseGuide?.content || []), ''];
    updateDocumentData('chineseGuide.content', newContent);
    message.success('已添加新导读条目');
  };

  // 删除中文导读条目
  const deleteChineseGuideItem = (index) => {
    const newContent = documentData.chineseGuide.content.filter((_, i) => i !== index);
    updateDocumentData('chineseGuide.content', newContent);
    message.success('导读条目已删除');
  };

  // 更新短语积累
  const updatePhraseItem = (index, value) => {
    const newItems = [...(documentData.phraseCollection?.items || [])];
    newItems[index] = { content: value };
    updateDocumentData('phraseCollection.items', newItems);
  };

  // 添加短语
  const addPhraseItem = () => {
    const newItems = [...(documentData.phraseCollection?.items || []), { content: '' }];
    updateDocumentData('phraseCollection.items', newItems);
    message.success('已添加新短语');
  };

  // 删除短语
  const deletePhraseItem = (index) => {
    const newItems = documentData.phraseCollection.items.filter((_, i) => i !== index);
    updateDocumentData('phraseCollection.items', newItems);
    message.success('短语已删除');
  };

  // 更新段落大意
  const updateSummaryItem = (index, value) => {
    const newItems = [...(documentData.paragraphSummary?.items || [])];
    newItems[index] = { content: value };
    updateDocumentData('paragraphSummary.items', newItems);
  };

  // 添加段落大意
  const addSummaryItem = () => {
    const newItems = [...(documentData.paragraphSummary?.items || []), { content: '' }];
    updateDocumentData('paragraphSummary.items', newItems);
    message.success('已添加新段落大意');
  };

  // 删除段落大意
  const deleteSummaryItem = (index) => {
    const newItems = documentData.paragraphSummary.items.filter((_, i) => i !== index);
    updateDocumentData('paragraphSummary.items', newItems);
    message.success('段落大意已删除');
  };

  if (!documentData) {
    return (
      <Card title="PDF内容编辑器">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary">正在加载文档数据...</Text>
        </div>
      </Card>
    );
  }

  // 段落单词快速预览组件
  const renderQuickWordPreview = () => {
    if (!documentData?.paragraphs || documentData.paragraphs.length === 0) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '20px',
          color: '#999'
        }}>
          <TagsOutlined style={{ fontSize: '20px', marginBottom: '8px' }} />
          <div>暂无段落单词</div>
        </div>
      );
    }

    return (
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {documentData.paragraphs.map((paragraph, paragraphIndex) => {
          const hasWords = paragraph.words && paragraph.words.length > 0;
          const hasUnderlinedWords = paragraph.underlinedWords && paragraph.underlinedWords.length > 0;

          if (!hasWords && !hasUnderlinedWords) return null;

          return (
            <div key={paragraphIndex} style={{
              border: '1px solid #f0f0f0',
              borderRadius: '6px',
              padding: '12px',
              background: '#fafafa'
            }}>
              <div style={{
                fontWeight: '500',
                marginBottom: '8px',
                color: '#1890ff'
              }}>
                段落 {paragraphIndex + 1}
                <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                  (重点单词: {paragraph.words?.length || 0}, 划线单词: {paragraph.underlinedWords?.length || 0})
                </span>
              </div>

              {/* 重点单词 */}
              {hasWords && (
                <div style={{ marginBottom: hasUnderlinedWords ? '12px' : '0' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    🔥 重点单词:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {paragraph.words.map((word, wordIndex) => (
                      <Tag
                        key={`preview-word-${paragraphIndex}-${wordIndex}-${word.name}`}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          handleWordDelete(paragraphIndex, wordIndex);
                        }}
                        style={{
                          margin: '2px',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontSize: '12px'
                        }}
                        color="blue"
                      >
                        <Tooltip title={`${word.name} - ${word.scopeWord || '暂无释义'}`}>
                          {word.name}
                        </Tooltip>
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* 自定义划线单词 */}
              {hasUnderlinedWords && (
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    📝 自定义划线:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {paragraph.underlinedWords.map((word, wordIndex) => (
                      <Tag
                        key={`preview-underlined-${paragraphIndex}-${wordIndex}-${word}`}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          removeUnderlinedWord(paragraphIndex, wordIndex);
                        }}
                        style={{
                          margin: '2px',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontSize: '12px'
                        }}
                        color="orange"
                      >
                        {word}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        }).filter(Boolean)}
      </Space>
    );
  };

  // 段落预览组件 - 移除useMemo避免Hook规则问题
  const renderParagraphPreview = () => {
    // 如果没有段落数据，返回空状态提示
    if (!documentData?.paragraphs || documentData.paragraphs.length === 0) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#999'
        }}>
          <BookOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
          <div>暂无段落数据</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            请先在其他面板中添加段落内容
          </div>
        </div>
      );
    }

    return (
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {documentData.paragraphs.map((paragraph, paragraphIndex) => (
          <Card
            key={paragraphIndex}
            size="small"
            title={`段落 ${paragraphIndex + 1}`}
            style={{ marginBottom: 8 }}
          >
            <div style={{ marginBottom: 12 }}>
              <Text strong>英文：</Text>
              <div style={{
                background: '#f5f5f5',
                padding: 8,
                borderRadius: 4,
                marginTop: 4,
                fontSize: '12px',
                lineHeight: 1.5
              }}>
                {paragraph.english}
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <Text strong>中文：</Text>
              <div style={{
                background: '#f5f5f5',
                padding: 8,
                borderRadius: 4,
                marginTop: 4,
                fontSize: '12px',
                lineHeight: 1.5
              }}>
                {paragraph.chinese}
              </div>
            </div>

            {paragraph.words && paragraph.words.length > 0 && (
              <div>
                <Text strong>重点单词：</Text>
                <div style={{ marginTop: 8 }}>
                  {paragraph.words.map((word, wordIndex) => (
                    <Tag
                      key={`edit-word-${paragraphIndex}-${wordIndex}-${word.name}`}
                      closable
                      onClose={() => handleWordDelete(paragraphIndex, wordIndex)}
                      style={{
                        margin: '2px 4px 2px 0',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                      onDoubleClick={() => handleWordEdit(paragraphIndex, wordIndex, word)}
                    >
                      <Tooltip title="双击编辑，点击×删除">
                        {word.name}
                      </Tooltip>
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ))}
      </Space>
    );
  };

  return (
    <Card
      title={<Title level={4}>📋 PDF内容编辑器</Title>}
      style={{
        height: '100%',
        overflow: 'auto'
      }}
      styles={{
        body: { padding: '16px 12px' }
      }}
    >
      <Form form={form} layout="vertical">
        <Collapse
          size="small"
          activeKey={activeKeys}
          onChange={setActiveKeys}
          ghost
          items={[
            // 段落单词快速预览面板
            {
              key: 'quick-words',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TagsOutlined />
                  <span>🏷️ 单词快速预览</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    (重点: {documentData?.paragraphs?.reduce((total, p) => total + (p.words?.length || 0), 0) || 0},
                     划线: {documentData?.paragraphs?.reduce((total, p) => total + (p.underlinedWords?.length || 0), 0) || 0})
                  </span>
                </div>
              ),
              children: renderQuickWordPreview()
            },
            // 段落预览面板
            {
              key: 'preview',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BookOutlined />
                  <span>📖 段落预览</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    ({documentData?.paragraphs?.length || 0}个段落)
                  </span>
                </div>
              ),
              children: renderParagraphPreview()
            },
            // 基础信息面板
            {
              key: 'basic',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PictureOutlined />
                  <span>🎯 基础信息</span>
                </div>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {/* Logo上传 */}
                  <div>
                    <div style={{ marginBottom: '8px', fontWeight: '500' }}>页头Logo</div>
                    <div>
                      <div style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        padding: '8px',
                        marginBottom: '8px',
                        textAlign: 'center',
                        background: '#fafafa',
                        position: 'relative'
                      }}>
                        <Image
                          src={logoPreview}
                          alt="Logo预览"
                          style={{ maxWidth: '100%', maxHeight: '60px' }}
                          preview={true}
                        />
                        {logoPreview === DEFAULT_IMAGES.logo && (
                          <div style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px'
                          }}>
                            默认
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          customRequest={({ onSuccess }) => {
                            setTimeout(() => onSuccess("ok"), 0);
                          }}
                          onChange={handleLogoUpload}
                          style={{ flex: 1 }}
                        >
                          <Button icon={<UploadOutlined />} block>
                            {logoPreview === DEFAULT_IMAGES.logo ? '上传Logo' : '更换Logo'}
                          </Button>
                        </Upload>
                        {logoPreview !== DEFAULT_IMAGES.logo && (
                          <Button
                            icon={<DeleteOutlined />}
                            onClick={handleLogoRemove}
                            danger
                            size="default"
                          >
                            恢复默认
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 页头标题 */}
                  <div>
                    <div style={{ marginBottom: '8px', fontWeight: '500' }}>页头标题</div>
                    <Input
                      placeholder="请输入页头标题"
                      value={getInputValue('header.text')}
                      onChange={(e) => handleLocalInput('header.text', e.target.value)}
                    />
                  </div>

                  {/* 文档标题 */}
                  <Row gutter={12}>
                    <Col span={24}>
                      <div style={{ marginBottom: '8px', fontWeight: '500' }}>英文标题</div>
                      <Input
                        placeholder="English Title"
                        value={getInputValue('title.english')}
                        onChange={(e) => handleLocalInput('title.english', e.target.value)}
                      />
                    </Col>
                    <Col span={24}>
                      <div style={{ marginBottom: '8px', fontWeight: '500' }}>中文标题</div>
                      <Input
                        placeholder="中文标题"
                        value={getInputValue('title.chinese')}
                        onChange={(e) => handleLocalInput('title.chinese', e.target.value)}
                      />
                    </Col>
                  </Row>

                  {/* 背景图片 */}
                  <div>
                    <div style={{ marginBottom: '8px', fontWeight: '500' }}>背景图片</div>
                    <div>
                      <div style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        padding: '8px',
                        marginBottom: '8px',
                        textAlign: 'center',
                        background: '#fafafa',
                        position: 'relative'
                      }}>
                        <Image
                          src={backgroundPreview}
                          alt="背景图片预览"
                          style={{ maxWidth: '100%', maxHeight: '120px' }}
                          preview={true}
                        />
                        {backgroundPreview === DEFAULT_IMAGES.background && (
                          <div style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px'
                          }}>
                            默认
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          customRequest={({ onSuccess }) => {
                            setTimeout(() => onSuccess("ok"), 0);
                          }}
                          onChange={handleBackgroundUpload}
                          style={{ flex: 1 }}
                        >
                          <Button icon={<UploadOutlined />} block>
                            {backgroundPreview === DEFAULT_IMAGES.background ? '上传背景图片' : '更换背景图片'}
                          </Button>
                        </Upload>
                        {backgroundPreview !== DEFAULT_IMAGES.background && (
                          <Button
                            icon={<DeleteOutlined />}
                            onClick={handleBackgroundRemove}
                            danger
                            size="default"
                          >
                            恢复默认
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Space>
              )
            },
            {
              key: 'content',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileTextOutlined />
                  <span>📝 主要内容</span>
                </div>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {/* 摘要信息 */}
                  <div>
                    <div style={{ marginBottom: '8px', fontWeight: '500' }}>摘要信息</div>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Input
                          placeholder="摘要标题"
                          value={getInputValue('summary.title')}
                          onChange={(e) => handleLocalInput('summary.title', e.target.value)}
                        />
                      </Col>
                      <Col span={12}>
                        <Input
                          placeholder="文章来源"
                          value={getInputValue('summary.source')}
                          onChange={(e) => handleLocalInput('summary.source', e.target.value)}
                        />
                      </Col>
                    </Row>
                  </div>

                  {/* 中文导读 */}
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontWeight: '500' }}>中文导读</span>
                      <Button
                        type="dashed"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={addChineseGuideItem}
                      >
                        添加条目
                      </Button>
                    </div>
                    <Input
                      placeholder="导读标题"
                      value={getInputValue('chineseGuide.title')}
                      onChange={(e) => handleLocalInput('chineseGuide.title', e.target.value)}
                      style={{ marginBottom: '8px' }}
                    />
                    {(documentData.chineseGuide?.content || []).map((item, index) => (
                      <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <TextArea
                          placeholder={`导读内容 ${index + 1}`}
                          value={item}
                          onChange={(e) => updateChineseGuideContent(index, e.target.value)}
                          autoSize={{ minRows: 2, maxRows: 4 }}
                          style={{ flex: 1 }}
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                          onClick={() => deleteChineseGuideItem(index)}
                          title="删除导读"
                        />
                      </div>
                    ))}
                  </div>

                  {/* 段落管理 - 手风琴模式 */}
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontWeight: '500' }}>段落管理</span>
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={addParagraph}
                      >
                        添加段落
                      </Button>
                    </div>
                    <Collapse
                      size="small"
                      activeKey={expandedParagraph !== null ? [`paragraph-${expandedParagraph}`] : []}
                      onChange={(keys) => {
                        // 只允许展开一个段落
                        if (keys.length > 0) {
                          const key = keys[keys.length - 1]; // 取最后一个key
                          const index = parseInt(key.replace('paragraph-', ''));
                          setExpandedParagraph(index);
                        } else {
                          setExpandedParagraph(null);
                        }
                      }}
                      items={(documentData.paragraphs || []).map((paragraph, index) => ({
                        key: `paragraph-${index}`,
                        label: (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span>段落 {index + 1} {paragraph.english ? `- ${paragraph.english.substring(0, 30)}${paragraph.english.length > 30 ? '...' : ''}` : '(空)'}</span>
                            <Popconfirm
                              title="确定删除这个段落吗？"
                              onConfirm={(e) => {
                                e.stopPropagation();
                                deleteParagraph(index);
                                setExpandedParagraph(null);
                              }}
                              okText="确定"
                              cancelText="取消"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Popconfirm>
                          </div>
                        ),
                        children: (
                          <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            {/* 段落内容编辑区域 */}
                            <div>
                              <div style={{
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#1890ff',
                                marginBottom: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                📝 段落内容
                              </div>
                              <Space direction="vertical" style={{ width: '100%' }} size="small">
                                <TextArea
                                  placeholder="英文段落"
                                  value={paragraph.english || ''}
                                  onChange={(e) => updateParagraph(index, 'english', e.target.value)}
                                  autoSize={{ minRows: 3, maxRows: 6 }}
                                />
                                <TextArea
                                  placeholder="中文段落"
                                  value={paragraph.chinese || ''}
                                  onChange={(e) => updateParagraph(index, 'chinese', e.target.value)}
                                  autoSize={{ minRows: 2, maxRows: 4 }}
                                />
                              </Space>
                            </div>

                            {/* 分隔线 */}
                            <div style={{
                              height: '1px',
                              background: 'linear-gradient(to right, transparent, #e8e8e8, transparent)',
                              margin: '8px 0'
                            }} />

                            {/* 自定义划线单词功能 */}
                            <div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px'
                              }}>
                                <span style={{
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: '#fa8c16',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  📝 自定义划线单词
                                </span>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  ({paragraph.underlinedWords?.length || 0}个)
                                </Text>
                              </div>

                              {/* 添加划线单词输入框 */}
                              <Input
                                placeholder="输入单词，用逗号或空格分隔，按回车添加"
                                value={underlinedWordInputs[index] || ''}
                                onChange={(e) => {
                                  setUnderlinedWordInputs(prev => ({
                                    ...prev,
                                    [index]: e.target.value
                                  }));
                                }}
                                onPressEnter={() => {
                                  const value = (underlinedWordInputs[index] || '').trim();
                                  if (value) {
                                    addUnderlinedWords(index, value);
                                    setUnderlinedWordInputs(prev => ({
                                      ...prev,
                                      [index]: ''
                                    }));
                                  }
                                }}
                                suffix={
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={() => {
                                      const value = (underlinedWordInputs[index] || '').trim();
                                      if (value) {
                                        addUnderlinedWords(index, value);
                                        setUnderlinedWordInputs(prev => ({
                                          ...prev,
                                          [index]: ''
                                        }));
                                      }
                                    }}
                                  />
                                }
                                style={{ marginBottom: '8px' }}
                              />

                              {/* 显示已添加的划线单词 */}
                              {paragraph.underlinedWords && paragraph.underlinedWords.length > 0 && (
                                <div style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '4px',
                                  marginBottom: '8px'
                                }}>
                                  {paragraph.underlinedWords.map((word, wordIndex) => (
                                    <Tag
                                      key={`underlined-${index}-${wordIndex}-${word}`}
                                      closable
                                      onClose={() => removeUnderlinedWord(index, wordIndex)}
                                      color="orange"
                                      style={{ fontSize: '12px' }}
                                    >
                                      {word}
                                    </Tag>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* 分隔线 */}
                            <div style={{
                              height: '1px',
                              background: 'linear-gradient(to right, transparent, #e8e8e8, transparent)',
                              margin: '16px 0'
                            }} />

                            {/* 重点单词管理 - 统一界面 */}
                            <div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px'
                              }}>
                                <span style={{
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: '#52c41a',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  📚 重点单词管理
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    ({paragraph.words?.length || 0}个)
                                  </Text>
                                  <Button
                                    type="dashed"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={() => addWordToParagraph(index)}
                                  >
                                    添加单词
                                  </Button>
                                </div>
                              </div>

                              {/* 重点单词列表 */}
                              {paragraph.words && paragraph.words.length > 0 ? (
                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                  {paragraph.words.map((word, wordIndex) => (
                                    <Card
                                      key={`edit-word-${index}-${wordIndex}-${word.name}`}
                                      size="small"
                                      type="inner"
                                      title={
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span style={{ fontSize: '14px' }}>
                                            单词 {wordIndex + 1}: {word.name || '(未命名)'}
                                          </span>
                                          <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            size="small"
                                            onClick={() => deleteWord(index, wordIndex)}
                                            title="删除单词"
                                          />
                                        </div>
                                      }
                                      style={{ marginBottom: '8px' }}
                                    >
                                      <Row gutter={[8, 8]}>
                                        <Col span={24}>
                                          <Input
                                            placeholder="单词"
                                            value={word.name || ''}
                                            onChange={(e) => updateWord(index, wordIndex, 'name', e.target.value)}
                                            addonBefore="单词"
                                          />
                                        </Col>
                                        <Col span={24}>
                                          <Input
                                            placeholder="音标"
                                            value={word.phonetic || ''}
                                            onChange={(e) => updateWord(index, wordIndex, 'phonetic', e.target.value)}
                                            addonBefore="音标"
                                          />
                                        </Col>
                                        {/* 备用音标字段 - 只有在有内容时才显示 */}
                                        {(word.alternativePhonetic || editingWordField === `${index}-${wordIndex}-alternativePhonetic`) && (
                                          <Col span={24}>
                                            <Input
                                              placeholder="备用音标（可选）"
                                              value={word.alternativePhonetic || ''}
                                              onChange={(e) => updateWord(index, wordIndex, 'alternativePhonetic', e.target.value)}
                                              addonBefore="备用音标"
                                              onFocus={() => setEditingWordField(`${index}-${wordIndex}-alternativePhonetic`)}
                                              onBlur={() => setEditingWordField(null)}
                                            />
                                          </Col>
                                        )}
                                        {/* 显示备用音标输入按钮 - 只有在没有内容且不在编辑状态时显示 */}
                                        {!word.alternativePhonetic && editingWordField !== `${index}-${wordIndex}-alternativePhonetic` && (
                                          <Col span={24}>
                                            <Button
                                              type="dashed"
                                              size="small"
                                              onClick={() => setEditingWordField(`${index}-${wordIndex}-alternativePhonetic`)}
                                              style={{ width: '100%', marginTop: '4px' }}
                                            >
                                              + 添加备用音标
                                            </Button>
                                          </Col>
                                        )}
                                        <Col span={24}>
                                          <Input
                                            placeholder="词性"
                                            value={word.partOfSpeech || ''}
                                            onChange={(e) => updateWord(index, wordIndex, 'partOfSpeech', e.target.value)}
                                            addonBefore="词性"
                                          />
                                        </Col>
                                        <Col span={24}>
                                          <Input
                                            placeholder="考试范围"
                                            value={word.scopeWord || ''}
                                            onChange={(e) => updateWord(index, wordIndex, 'scopeWord', e.target.value)}
                                            addonBefore="范围"
                                          />
                                        </Col>
                                        <Col span={24}>
                                          <TextArea
                                            placeholder="英文例句"
                                            value={word.example || ''}
                                            onChange={(e) => updateWord(index, wordIndex, 'example', e.target.value)}
                                            autoSize={{ minRows: 2, maxRows: 3 }}
                                          />
                                        </Col>
                                        <Col span={24}>
                                          <Input
                                            placeholder="例句中要高亮的单词形式（如：retiring, retired 等变形）"
                                            value={word.highlightWord || ''}
                                            onChange={(e) => updateWord(index, wordIndex, 'highlightWord', e.target.value)}
                                            addonBefore="高亮词"
                                            allowClear
                                          />
                                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                            💡 留空则高亮单词原形 "{word.name}"，填写则高亮指定形式
                                          </div>
                                        </Col>
                                        <Col span={24}>
                                          <TextArea
                                            placeholder="中文例句"
                                            value={word.chExample || ''}
                                            onChange={(e) => updateWord(index, wordIndex, 'chExample', e.target.value)}
                                            autoSize={{ minRows: 2, maxRows: 3 }}
                                          />
                                        </Col>
                                      </Row>
                                    </Card>
                                  ))}
                                </Space>
                              ) : (
                                <div style={{
                                  textAlign: 'center',
                                  padding: '20px',
                                  color: '#999',
                                  background: '#fafafa',
                                  borderRadius: '4px',
                                  border: '1px dashed #d9d9d9'
                                }}>
                                  暂无重点单词，点击"添加单词"按钮添加
                                </div>
                              )}
                            </div>
                          </Space>
                        )
                      }))}
                    />
                  </div>
                </Space>
              )
            },
            {
              key: 'additional',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BulbOutlined />
                  <span>📖 附加内容</span>
                </div>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {/* 短语积累 */}
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontWeight: '500' }}>短语积累</span>
                      <Button
                        type="dashed"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={addPhraseItem}
                      >
                        添加短语
                      </Button>
                    </div>
                    <Input
                      placeholder="短语积累标题"
                      value={getInputValue('phraseCollection.title')}
                      onChange={(e) => handleLocalInput('phraseCollection.title', e.target.value)}
                      style={{ marginBottom: '8px' }}
                    />
                    {(documentData.phraseCollection?.items || []).map((item, index) => (
                      <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <Input
                          placeholder={`短语 ${index + 1}`}
                          value={item.content || ''}
                          onChange={(e) => updatePhraseItem(index, e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                          onClick={() => deletePhraseItem(index)}
                          title="删除短语"
                        />
                      </div>
                    ))}
                  </div>

                  {/* 段落大意 */}
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontWeight: '500' }}>段落大意</span>
                      <Button
                        type="dashed"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={addSummaryItem}
                      >
                        添加大意
                      </Button>
                    </div>
                    <Input
                      placeholder="段落大意标题"
                      value={getInputValue('paragraphSummary.title')}
                      onChange={(e) => handleLocalInput('paragraphSummary.title', e.target.value)}
                      style={{ marginBottom: '8px' }}
                    />
                    {(documentData.paragraphSummary?.items || []).map((item, index) => (
                      <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <TextArea
                          placeholder={`段落大意 ${index + 1}`}
                          value={item.content || ''}
                          onChange={(e) => updateSummaryItem(index, e.target.value)}
                          autoSize={{ minRows: 2, maxRows: 4 }}
                          style={{ flex: 1 }}
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                          onClick={() => deleteSummaryItem(index)}
                          title="删除大意"
                        />
                      </div>
                    ))}
                  </div>
                </Space>
              )
            },
            {
              key: 'watermark',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>💧</span>
                  <span>🔒</span>
                  <span>水印设置</span>
                </div>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {/* 水印开关 */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: '500' }}>启用水印</span>
                    <Switch
                      checked={watermarkConfig?.enabled || false}
                      onChange={(checked) => setWatermarkConfig(prev => ({ ...prev, enabled: checked }))}
                    />
                  </div>

                  {/* 水印配置 - 仅在启用时显示 */}
                  {watermarkConfig?.enabled && (
                    <>
                      {/* 水印文本 */}
                      <div>
                        <span style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>水印文本</span>
                        <Input
                          placeholder="请输入水印文本"
                          value={watermarkConfig.text}
                          onChange={(e) => setWatermarkConfig(prev => ({ ...prev, text: e.target.value }))}
                        />
                      </div>

                      {/* 透明度 */}
                      <div>
                        <span style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                          透明度: {Math.round(watermarkConfig.opacity * 100)}%
                        </span>
                        <Slider
                          min={0.1}
                          max={0.8}
                          step={0.1}
                          value={watermarkConfig.opacity}
                          onChange={(value) => setWatermarkConfig(prev => ({ ...prev, opacity: value }))}
                        />
                      </div>

                      {/* 旋转角度 */}
                      <div>
                        <span style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                          旋转角度: {watermarkConfig.rotation}°
                        </span>
                        <Slider
                          min={-90}
                          max={90}
                          step={15}
                          value={watermarkConfig.rotation}
                          onChange={(value) => setWatermarkConfig(prev => ({ ...prev, rotation: value }))}
                          marks={{
                            '-45': '-45°',
                            '0': '0°',
                            '45': '45°'
                          }}
                        />
                      </div>

                      {/* 字体大小 */}
                      <div>
                        <span style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                          字体大小: {watermarkConfig.fontSize}px
                        </span>
                        <Slider
                          min={12}
                          max={48}
                          step={2}
                          value={watermarkConfig.fontSize}
                          onChange={(value) => setWatermarkConfig(prev => ({ ...prev, fontSize: value }))}
                        />
                      </div>

                      {/* 水印密度 */}
                      <div>
                        <span style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>水印密度</span>
                        <Select
                          value={watermarkConfig.density}
                          onChange={(value) => setWatermarkConfig(prev => ({ ...prev, density: value }))}
                          style={{ width: '100%' }}
                        >
                          <Select.Option value="low">稀疏</Select.Option>
                          <Select.Option value="medium">中等</Select.Option>
                          <Select.Option value="high">密集</Select.Option>
                        </Select>
                      </div>

                      {/* 水印颜色 */}
                      <div>
                        <span style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>水印颜色</span>
                        <Input
                          type="color"
                          value={watermarkConfig.color}
                          onChange={(e) => setWatermarkConfig(prev => ({ ...prev, color: e.target.value }))}
                          style={{ width: '100%', height: '32px' }}
                        />
                      </div>

                      {/* 水印层级 */}
                      <div>
                        <span style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>显示层级</span>
                        <Select
                          value={watermarkConfig.layer}
                          onChange={(value) => setWatermarkConfig(prev => ({ ...prev, layer: value }))}
                          style={{ width: '100%' }}
                        >
                          <Select.Option value="front">最前层（覆盖内容）</Select.Option>
                          <Select.Option value="back">最后层（内容覆盖）</Select.Option>
                        </Select>
                        <div style={{
                          fontSize: '12px',
                          color: '#666',
                          marginTop: '4px',
                          lineHeight: '1.4'
                        }}>
                          {watermarkConfig.layer === 'front'
                            ? '💡 水印将显示在所有内容之上'
                            : '💡 水印将显示在所有内容之下'}
                        </div>
                      </div>
                    </>
                  )}
                </Space>
              )
            },
            {
              key: 'data-manager',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileTextOutlined />
                  <span>📊 数据管理</span>
                </div>
              ),
              children: (
                <DataManager
                  documentData={documentData}
                  onDocumentDataChange={onDocumentDataChange}
                />
              )
            }
          ]}
        />
      </Form>

      {/* 单词编辑模态框 */}
      <Modal
        title="编辑单词"
        open={editWordModal}
        onOk={handleWordSave}
        onCancel={() => {
          setEditWordModal(false);
          setEditingWord(null);
        }}
        width={600}
      >
        {editingWord && (
          <Form layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="单词">
                  <Input
                    value={editingWord.word.name}
                    onChange={(e) => setEditingWord(prev => ({
                      ...prev,
                      word: { ...prev.word, name: e.target.value }
                    }))}
                    placeholder="请输入单词"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="音标">
                  <Input
                    value={editingWord.word.phonetic}
                    onChange={(e) => setEditingWord(prev => ({
                      ...prev,
                      word: { ...prev.word, phonetic: e.target.value }
                    }))}
                    placeholder="请输入音标"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="备用音标">
                  <Input
                    value={editingWord.word.alternativePhonetic || ''}
                    onChange={(e) => setEditingWord(prev => ({
                      ...prev,
                      word: { ...prev.word, alternativePhonetic: e.target.value }
                    }))}
                    placeholder="请输入备用音标（可选）"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="词性">
                  <Input
                    value={editingWord.word.partOfSpeech}
                    onChange={(e) => setEditingWord(prev => ({
                      ...prev,
                      word: { ...prev.word, partOfSpeech: e.target.value }
                    }))}
                    placeholder="请输入词性"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="释义">
                  <Input
                    value={editingWord.word.scopeWord}
                    onChange={(e) => setEditingWord(prev => ({
                      ...prev,
                      word: { ...prev.word, scopeWord: e.target.value }
                    }))}
                    placeholder="请输入释义"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="例句">
              <TextArea
                value={editingWord.word.example}
                onChange={(e) => setEditingWord(prev => ({
                  ...prev,
                  word: { ...prev.word, example: e.target.value }
                }))}
                placeholder="请输入例句"
                rows={2}
              />
            </Form.Item>
            <Form.Item label="中文例句">
              <TextArea
                value={editingWord.word.chExample}
                onChange={(e) => setEditingWord(prev => ({
                  ...prev,
                  word: { ...prev.word, chExample: e.target.value }
                }))}
                placeholder="请输入中文例句"
                rows={2}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Card>
  );
};

// 使用React.memo优化性能
export default React.memo(EnhancedControlPanel, (prevProps, nextProps) => {
  // 自定义比较函数，只在关键数据变化时重新渲染
  return (
    prevProps.documentData === nextProps.documentData &&
    prevProps.watermarkConfig === nextProps.watermarkConfig &&
    prevProps.onDocumentDataChange === nextProps.onDocumentDataChange &&
    prevProps.setWatermarkConfig === nextProps.setWatermarkConfig
  );
});
